<template>
  <div class="profile-register-form">
    <FormKit type="form" @submit="submitForm" :disabled="isLoading" :errors="errors">
      <FormKit
        :errors="data.psdata?.firstname"
        type="text"
        name="firstname"
        v-model="form.firstname"
        :label="t('First name')"
        validation="required"
      />
      <FormKit
        type="text"
        name="lastname"
        :errors="data.psdata?.lastname"
        v-model="form.lastname"
        :label="t('Last name')"
        validation="required"
      />
      <FormKit
        type="email"
        :errors="data.psdata?.email"
        name="email"
        v-model="form.email"
        :label="t('Email')"
        validation="required"
      />
      <FormKit
        type="password"
        :errors="data.psdata?.password"
        name="password"
        v-model="form.password"
        :label="t('Password')"
        validation="required|strongPassword"
      />
      <PasswordStrengthIndicator 
        :password="form.password"
        v-if="form.password"
        class="w-full"
      />
      <FormKit
        type="password"
        name="confirmPassword"
        v-model="form.confirmPassword"
        :label="t('Confirm Password')"
        :validation="[['required'], ['passwordMatch', form.password]]"
      />
      <FormKit
        :errors="data.psdata?.newsletter"
        type="checkbox"
        name="newsletter"
        v-model="form.newsletter"
        :label="t('Sign up for our newsletter')"
      />
      
      <!-- Language Preference - Show only if newsletter is checked -->
      <div v-if="form.newsletter" class="space-y-2 mt-4 mb-4 max-w-sm">
        <label class="formkit-label text-sm font-medium">
          {{ t('newsletter.language_label') }} <span class="text-red-500">*</span>
        </label>
        <Select v-model="form.newsletter_language" :disabled="isLoading">
          <SelectTrigger>
            <SelectValue :placeholder="t('newsletter.language_label')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="it">
              {{ t('newsletter.language_italian') }}
            </SelectItem>
            <SelectItem value="en">
              {{ t('newsletter.language_english') }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <FormKit
        type="checkbox"
        :errors="data.psdata?.customer_privacy"
        name="customer_privacy"
        v-model="form.customer_privacy"
        :label="t('Customer data privacy')"
      />
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import { FormKit } from '@formkit/vue';
import useNotifications from '~/stores/notifications';
import { useSdk } from '~/sdk';
import PasswordStrengthIndicator from '~/components/PasswordStrengthIndicator.vue';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';

const emit = defineEmits(['success']);
const config = inject(Symbol.for('FormKitConfig')) as any
const { locale } = useI18n();
const router = useRouter();
const localePath = useLocalePath();
const { send } = useNotifications();
const { refresh } = useAuth()

config.locale = locale.value;

const form = ref({
  email: '',
  password: '',
  confirmPassword: '',
  firstname: '',
  lastname: '',
  psgdpr: false,
  newsletter: false,
  customer_privacy: false,
  newsletter_language: '',
});

const data = ref<any>({});
const errors = ref<any>([]);
const isLoading = ref(false);
const { t } = useI18n();

const submitForm = async () => {
  isLoading.value = true;
  errors.value = [];

  const res = await useSdk().commerce.register({
    firstname: form.value.firstname,
    lastname: form.value.lastname,
    email: form.value.email,
    password: form.value.password,
    newsletter: form.value.newsletter,
    customer_privacy: form.value.customer_privacy,
    mailerlite_newsletter: form.value.newsletter,
    newsletter_language: form.value.newsletter ? form.value.newsletter_language : undefined,
  });

  data.value = res;

  if (!res.success) {
    errors.value = [...errors.value, ...(data.value.psdata?.list ?? [])];
    isLoading.value = false;
    return;
  }

  send({
    message: t('The user account data was successfully updated!'),
    type: 'success',
  });

  await refresh();

  isLoading.value = false;

  emit('success');
};
</script>
